import { StateCreator } from "zustand";
import { StoreState } from "..";

export type ViewMode = "table" | "card";

const VIEW_MODE_STORAGE_PREFIX = "crolytics-dashboard-view-mode";
const DEFAULT_VIEW_MODE: ViewMode = "table";
const VALID_VIEW_MODES: ViewMode[] = ["table", "card"];

function getViewModeStorageKey(userId: string) {
  return `${VIEW_MODE_STORAGE_PREFIX}:${userId}`;
}

function isViewMode(value: string | null): value is ViewMode {
  return value != null && VALID_VIEW_MODES.includes(value as ViewMode);
}

function canUseStorage() {
  return typeof window !== "undefined" && typeof localStorage !== "undefined";
}

export type FocusedItem =
  | { id: string; type: "page" | "idea" | "audit_job" | "ga_insight" }
  | null;
export type PendingWorkspaceSelection = {
  domainId: string;
  pageId: string;
  url: string;
} | null;

export interface UISlice {
  /* Modal States */
  viewMode: ViewMode;

  /* Focused Item (set by search bar, consumed by RecommendationsSection) */
  focusedItem: FocusedItem;
  pendingWorkspaceSelection: PendingWorkspaceSelection;

  /* Context Sidebar States */
  contextSidebarDismissedDomains: string[];

  /* Actions */
  dismissContextSidebarForDomain: (domainId: string) => void;
  isContextSidebarDismissedForDomain: (domainId: string) => boolean;
  hydrateViewMode: (userId: string | null) => void;
  setViewMode: (userId: string | null, mode: ViewMode) => void;
  setFocusedItem: (item: FocusedItem) => void;
  clearFocusedItem: () => void;
  setPendingWorkspaceSelection: (selection: PendingWorkspaceSelection) => void;
  clearPendingWorkspaceSelection: () => void;
  resetSessionState: () => void;
}

export const createUISlice: StateCreator<StoreState, [], [], UISlice> = (
  set,
  get,
) => ({
  /* Initial State */
  viewMode: DEFAULT_VIEW_MODE,
  focusedItem: null,
  pendingWorkspaceSelection: null,
  contextSidebarDismissedDomains: [],

  /* Actions */
  dismissContextSidebarForDomain: (domainId: string) => {
    const currentDismissed = get().contextSidebarDismissedDomains;
    if (!currentDismissed.includes(domainId)) {
      set({
        contextSidebarDismissedDomains: [...currentDismissed, domainId],
      });
    }
  },

  isContextSidebarDismissedForDomain: (domainId: string) => {
    return get().contextSidebarDismissedDomains.includes(domainId);
  },

  hydrateViewMode: (userId) => {
    if (!userId || !canUseStorage()) {
      set({ viewMode: DEFAULT_VIEW_MODE });
      return;
    }

    const storedMode = localStorage.getItem(getViewModeStorageKey(userId));

    set({
      viewMode: isViewMode(storedMode) ? storedMode : DEFAULT_VIEW_MODE,
    });
  },

  setViewMode: (userId, mode) => {
    set({ viewMode: mode });

    if (!userId || !canUseStorage()) {
      return;
    }

    localStorage.setItem(getViewModeStorageKey(userId), mode);
  },

  setFocusedItem: (item) => {
    set({ focusedItem: item });
  },

  clearFocusedItem: () => {
    set({ focusedItem: null });
  },

  setPendingWorkspaceSelection: (selection) => {
    set({ pendingWorkspaceSelection: selection });
  },

  clearPendingWorkspaceSelection: () => {
    set({ pendingWorkspaceSelection: null });
  },

  resetSessionState: () => {
    set({
      focusedItem: null,
      pendingWorkspaceSelection: null,
      contextSidebarDismissedDomains: [],
    });
  },
});
